import { inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';

/**
 * MSAL app initializer.
 *
 * `@azure/msal-browser` v5 refuses every redirect/token API until
 * `initialize()` has resolved — `handleRedirectPromise`, `loginRedirect` and
 * `acquireTokenSilent` all throw `uninitialized_public_client_application`.
 *
 * Doing this in the root component's `ngOnInit` is too late: the router's
 * initial navigation, and therefore the route guards and HTTP interceptors,
 * runs inside the same `bootstrapApplication` call. Going through
 * `provideAppInitializer` makes Angular await MSAL before the app starts, so
 * every one of those call sites sees an initialized instance.
 *
 * Ported from the Creator frontend, which has run this in production.
 */
export function initializeMsal(): Promise<void> {
  const instance = inject(MsalService).instance;

  return instance
    .initialize()
    .then(() => instance.handleRedirectPromise())
    .then((response) => {
      if (response?.account) {
        instance.setActiveAccount(response.account);
        return;
      }

      // No redirect response — adopt a cached account so a returning user is
      // not bounced through sign-in again on a hard refresh.
      if (!instance.getActiveAccount()) {
        const [cachedAccount] = instance.getAllAccounts();
        if (cachedAccount) {
          instance.setActiveAccount(cachedAccount);
        }
      }
    })
    .catch((error: unknown) => {
      // Never block bootstrap on an auth failure: a rejected promise here
      // leaves the user on a blank page instead of the sign-in redirect.
      console.error('MSAL initialization/redirect handling failed:', error);
    });
}
